import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Car,
  ArrowLeft,
  Edit,
  Trash2,
  MapPin,
  Users,
  Fuel,
  Calendar,
  Gauge,
  Palette,
  Cog,
  IndianRupee,
  Clock,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Wrench,
  CreditCard,
  Plus,
  Download,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Share2,
  Tag,
  AlertTriangle
} from 'lucide-react';
import { ownerLabel } from '../../lib/vehiclePurpose';
import ShareSheet from '../../components/ui/ShareSheet';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { formatCurrency, formatName, formatVehicleName, cldImg } from '../../lib/utils';
import { carAPI, documentAPI } from '../../services/api';
import CarLoader, { CircularCarLoader } from '../../components/ui/CarLoader';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Toast from '../../components/ui/Toast';

// Registration board (India) → display label + color
const BOARD_BADGE = {
  white: { label: '⬜ White Board · Private', cls: 'bg-gray-100 text-gray-700 border border-gray-300' },
  yellow: { label: '🟡 Yellow Board · Commercial', cls: 'bg-yellow-100 text-yellow-800 border border-yellow-300' },
  green: { label: '🟢 Green Board · Electric', cls: 'bg-green-100 text-green-700 border border-green-300' },
  black: { label: '⬛ Black Board · Self-drive Rental', cls: 'bg-gray-800 text-white' }
};

// Mirrors the public listing so an owner sees exactly what a buyer sees.
const LOAN_STATUS = {
  none: { text: 'No loan taken', cls: 'text-green-600' },
  closed: { text: 'Closed — NOC available', cls: 'text-green-600' },
  active: { text: 'Running — NOC pending', cls: 'text-red-600' },
};

const CONDITION_LABELS = {
  excellent: 'Excellent', good: 'Good', fair: 'Fair', 'needs-work': 'Needs Work',
};

const ordinalOwner = (n) => ownerLabel(n);

const saleDate = (d) => new Date(d).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

// Red once lapsed, amber inside two months — the owner should fix it before a buyer asks.
const expiryTone = (d) => {
  const days = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (days < 0) return { cls: 'text-red-600', note: 'Expired' };
  if (days <= 60) return { cls: 'text-amber-600', note: 'Expiring soon' };
  return { cls: 'text-gray-900', note: '' };
};

const CarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // read-only monitor: no edit/delete/create actions
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [mainImgLoading, setMainImgLoading] = useState(true);
  const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', type: 'danger', confirmText: 'Confirm', onConfirm: null });
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Preload all gallery images (at viewer size) once the car loads, so clicking
  // next/prev swaps instantly instead of waiting on Cloudinary to generate each.
  useEffect(() => {
    if (!car?.images?.length) return;
    car.images.forEach(url => {
      const img = new Image();
      img.src = cldImg(url, { w: 900 });
    });
  }, [car]);

  // When the shown image changes, only show the loader if the browser hasn't
  // already cached it. Cached images appear instantly — no spinner, no reload.
  useEffect(() => {
    const url = car?.images?.[selectedImage]
      ? cldImg(car.images[selectedImage], { w: 900 })
      : null;
    if (!url) return;
    const probe = new Image();
    probe.src = url;
    if (probe.complete) {
      setMainImgLoading(false); // already in cache
    } else {
      setMainImgLoading(true);
      probe.onload = () => setMainImgLoading(false);
      probe.onerror = () => setMainImgLoading(false);
    }
  }, [selectedImage, car]);

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Services state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceModal, setServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    serviceType: 'regular_service',
    serviceDate: new Date().toISOString().split('T')[0],
    description: '',
    serviceCenterName: '',
    odometerAtService: '',
    laborCost: '',
    partsCost: '',
    status: 'completed',
    notes: ''
  });

  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [expenseModal, setExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    category: 'fuel',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    paymentMethod: 'cash',
    vendor: '',
    receiptNumber: ''
  });

  // FASTag state
  const [fastag, setFastag] = useState(null);
  const [fastagLoading, setFastagLoading] = useState(false);
  const [fastagTransactions, setFastagTransactions] = useState([]);
  const [fastagModal, setFastagModal] = useState(false);
  const [fastagForm, setFastagForm] = useState({
    fastagId: '',
    provider: '',
    balance: ''
  });
  const [transactionModal, setTransactionModal] = useState(false);
  const [transactionForm, setTransactionForm] = useState({
    transactionType: 'toll',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });

  useEffect(() => {
    fetchCar();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (car && activeTab === 'documents') {
      fetchDocuments();
    } else if (car && activeTab === 'services') {
      fetchServices();
    } else if (car && activeTab === 'expenses') {
      fetchExpenses();
    } else if (car && activeTab === 'fastag') {
      fetchFastag();
    }
  }, [activeTab, car]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCar = async () => {
    try {
      setLoading(true);
      console.log('Fetching car with ID:', id);
      const response = await carAPI.getCarById(id);
      console.log('Car response:', response.data);
      setCar(response.data.car || response.data);
    } catch (error) {
      console.error('Error fetching car:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      setError(`Failed to load car details: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const response = await documentAPI.getCarDocuments(id);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      setServicesLoading(true);
      const response = await carAPI.getServices(id);
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      setExpensesLoading(true);
      const [expensesRes, summaryRes] = await Promise.all([
        carAPI.getExpenses(id),
        carAPI.getExpenseSummary(id)
      ]);
      setExpenses(expensesRes.data.expenses || []);
      setExpenseSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setExpensesLoading(false);
    }
  };

  const fetchFastag = async () => {
    try {
      setFastagLoading(true);
      const [fastagRes, transactionsRes] = await Promise.all([
        carAPI.getFastag(id),
        carAPI.getFastagTransactions(id)
      ]);
      setFastag(fastagRes.data.fastag);
      setFastagTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      console.error('Error fetching fastag:', error);
    } finally {
      setFastagLoading(false);
    }
  };

  // Service handlers
  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const totalCost = (parseFloat(serviceForm.laborCost) || 0) + (parseFloat(serviceForm.partsCost) || 0);
      await carAPI.addService(id, {
        ...serviceForm,
        odometerAtService: serviceForm.odometerAtService ? parseInt(serviceForm.odometerAtService) : undefined,
        laborCost: parseFloat(serviceForm.laborCost) || 0,
        partsCost: parseFloat(serviceForm.partsCost) || 0,
        totalCost
      });
      setServiceModal(false);
      setServiceForm({
        serviceType: 'regular_service',
        serviceDate: new Date().toISOString().split('T')[0],
        description: '',
        serviceCenterName: '',
        odometerAtService: '',
        laborCost: '',
        partsCost: '',
        status: 'completed',
        notes: ''
      });
      fetchServices();
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleDeleteService = async (serviceId) => {
    setConfirm({ isOpen: true, title: 'Delete Service Record?', message: 'Are you sure you want to delete this service record?', type: 'danger', confirmText: 'Delete', onConfirm: async () => {
      try {
        await carAPI.deleteService(id, serviceId);
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    } });
  };

  // Expense handlers
  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await carAPI.addExpense(id, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      setExpenseModal(false);
      setExpenseForm({
        category: 'fuel',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        paymentMethod: 'cash',
        vendor: '',
        receiptNumber: ''
      });
      fetchExpenses();
    } catch (error) {
      console.error('Error adding expense:', error);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    setConfirm({ isOpen: true, title: 'Delete Expense?', message: 'Are you sure you want to delete this expense?', type: 'danger', confirmText: 'Delete', onConfirm: async () => {
      try {
        await carAPI.deleteExpense(id, expenseId);
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    } });
  };

  // FASTag handlers
  const handleUpdateFastag = async (e) => {
    e.preventDefault();
    try {
      await carAPI.updateFastag(id, {
        ...fastagForm,
        balance: parseFloat(fastagForm.balance) || 0
      });
      setFastagModal(false);
      fetchFastag();
    } catch (error) {
      console.error('Error updating fastag:', error);
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await carAPI.addFastagTransaction(id, {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount)
      });
      setTransactionModal(false);
      setTransactionForm({
        transactionType: 'toll',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: ''
      });
      fetchFastag();
    } catch (error) {
      console.error('Error adding transaction:', error);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    setConfirm({ isOpen: true, title: 'Delete Transaction?', message: 'Are you sure you want to delete this transaction?', type: 'danger', confirmText: 'Delete', onConfirm: async () => {
      try {
        await carAPI.deleteFastagTransaction(id, transactionId);
        fetchFastag();
      } catch (error) {
        console.error('Error deleting transaction:', error);
      }
    } });
  };

  const handleDelete = async () => {
    try {
      await carAPI.deleteCar(id);
      navigate('/cars');
    } catch (error) {
      console.error('Error deleting car:', error);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <CarLoader message="Loading vehicle details..." />
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="text-center py-12">
        <Car className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">{error || 'Vehicle not found'}</p>
        <Button variant="outline" onClick={() => navigate('/cars')} className="mt-4">
          Back to Vehicles
        </Button>
      </div>
    );
  }

  const getServiceTypeLabel = (type) => {
    const labels = {
      'rental': 'Multi-Day Rental',
      'daily': 'Daily Rental',
      'km-based': 'Pay Per KM',
      'outstation': 'Outstation',
      'local': 'Local Trips',
      'sale': 'For Sale'
    };
    return labels[type] || type;
  };

  const getMaintenanceTypeLabel = (type) => {
    const labels = {
      'regular_service': 'Regular Service',
      'oil_change': 'Oil Change',
      'tire_change': 'Tire Change',
      'brake_service': 'Brake Service',
      'ac_service': 'AC Service',
      'battery': 'Battery',
      'engine': 'Engine',
      'electrical': 'Electrical',
      'body_repair': 'Body Repair',
      'painting': 'Painting',
      'washing': 'Washing',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  const getExpenseCategoryLabel = (category) => {
    const labels = {
      'fuel': 'Fuel',
      'service': 'Service',
      'repair': 'Repair',
      'insurance': 'Insurance',
      'tax': 'Tax',
      'fastag': 'FASTag',
      'parking': 'Parking',
      'fine': 'Fine',
      'other': 'Other'
    };
    return labels[category] || category;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Car },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'services', label: 'Service History', icon: Wrench },
    { id: 'expenses', label: 'Expenses', icon: IndianRupee },
    { id: 'fastag', label: 'FASTag', icon: CreditCard }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${formatName(car.make)} ${formatName(car.model)}`}
        description={[car.variant, car.year, car.registrationNumber].filter(Boolean).join(' · ')}
        action={
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => navigate('/cars')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <Button variant="outline" onClick={() => setShareOpen(true)}>
              <Share2 className="h-5 w-5 mr-2" />
              Share
            </Button>
            {!isAdmin && (
              <>
                <Button variant="outline" onClick={() => navigate(`/cars/edit/${car._id}`)}>
                  <Edit className="h-5 w-5 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" onClick={() => setDeleteModal(true)}>
                  <Trash2 className="h-5 w-5 mr-2" />
                  Delete
                </Button>
              </>
            )}
            <ShareSheet
              open={shareOpen}
              onClose={() => setShareOpen(false)}
              url={`https://wheels4rent.in/share/vehicle/${car._id}`}
              title={`${formatName(car.make)} ${formatName(car.model)}`}
              text={car.pricePerDay ? `₹${car.pricePerDay}/day` : 'Available for rent'}
              modes={(car.forSale && car.forRent !== false) ? [
                { key: 'rent', label: 'Share for Rent', url: `https://wheels4rent.in/share/vehicle/${car._id}?mode=rent`, text: car.pricePerDay ? `₹${car.pricePerDay}/day` : 'Available for rent' },
                { key: 'buy', label: 'Share for Buy', url: `https://wheels4rent.in/share/vehicle/${car._id}?mode=buy`, text: car.salePrice > 0 ? `For sale · ₹${Number(car.salePrice).toLocaleString('en-IN')}` : 'Available for sale' },
              ] : null}
            />
          </div>
        }
      />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Images & Specs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image Carousel */}
          <Card>
            <CardContent className="p-0">
              <div className="relative h-96 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center rounded-t-lg overflow-hidden">
                {car.images && car.images.length > 0 ? (
                  <>
                    <img
                      src={cldImg(car.images[selectedImage], { w: 900 })}
                      alt={formatVehicleName(car, { showYear: false })}
                      onLoad={() => setMainImgLoading(false)}
                      onError={() => setMainImgLoading(false)}
                      className={`w-full h-full object-cover transition-opacity duration-300 ${mainImgLoading ? 'opacity-30' : 'opacity-100'}`}
                    />
                    {/* Branded loader while the image is being fetched */}
                    {mainImgLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px] pointer-events-none">
                        <CircularCarLoader size={56} message="" />
                      </div>
                    )}
                    {/* Image Counter */}
                    <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {selectedImage + 1} / {car.images.length}
                    </div>
                    {/* Navigation Arrows */}
                    {car.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImage(prev => prev === 0 ? car.images.length - 1 : prev - 1)}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={() => setSelectedImage(prev => prev === car.images.length - 1 ? 0 : prev + 1)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <Car className="h-32 w-32 text-blue-400" />
                )}
              </div>
              {/* Image Thumbnails */}
              {car.images && car.images.length > 1 && (
                <div className="p-4 flex space-x-2 overflow-x-auto">
                  {car.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-400'
                      }`}
                    >
                      <img src={cldImg(img, { w: 150 })} loading="lazy" alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Specifications */}
          <Card>
            <CardHeader>
              <CardTitle>Specifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Seating</p>
                  <p className="font-semibold text-gray-900">{car.seats > 1 ? `${car.seats - 1}+1` : car.seats}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Fuel className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Fuel Type</p>
                  <p className="font-semibold text-gray-900">{car.fuelType}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Cog className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Transmission</p>
                  <p className="font-semibold text-gray-900">{car.transmission}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Palette className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Color</p>
                  <p className="font-semibold text-gray-900">{car.color || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Calendar className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Year</p>
                  <p className="font-semibold text-gray-900">{car.year}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Gauge className="h-6 w-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Mileage</p>
                  <p className="font-semibold text-gray-900">{car.mileage || 0} km/l</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <MapPin className="h-6 w-6 text-teal-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold text-gray-900">{car.location?.city || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Clock className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Min Booking</p>
                  <p className="font-semibold text-gray-900">{car.minBookingDays || 1} day(s)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resale Details — what a buyer sees on the public listing. Shown to the
              owner so they can spot a lapsed date or a missing answer themselves. */}
          {car.forSale && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5 text-red-600" /> Resale Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {car.saleDetails?.loanStatus === 'active' && (
                  <div className="flex items-start gap-2 mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-800">
                      <span className="font-semibold">Loan is still running.</span> Buyers are told ownership
                      cannot transfer until the bank issues an NOC.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-sm">
                  {car.variant && (
                    <div><p className="text-xs text-gray-500">Variant</p><p className="font-medium text-gray-900">{car.variant}</p></div>
                  )}
                  {car.saleDetails?.kmDriven != null && (
                    <div><p className="text-xs text-gray-500">KM Driven</p><p className="font-medium text-gray-900">{Number(car.saleDetails.kmDriven).toLocaleString('en-IN')} km</p></div>
                  )}
                  {car.saleDetails?.noOfOwners && (
                    <div><p className="text-xs text-gray-500">Ownership</p><p className="font-medium text-gray-900">{ordinalOwner(car.saleDetails.noOfOwners)}</p></div>
                  )}
                  {car.saleDetails?.condition && (
                    <div><p className="text-xs text-gray-500">Condition</p><p className="font-medium text-gray-900">{CONDITION_LABELS[car.saleDetails.condition] || car.saleDetails.condition}</p></div>
                  )}
                  {car.saleDetails?.registrationYear && (
                    <div><p className="text-xs text-gray-500">Reg. Year</p><p className="font-medium text-gray-900">{car.saleDetails.registrationYear}</p></div>
                  )}
                  {car.saleDetails?.insuranceValidTill && (() => {
                    const t = expiryTone(car.saleDetails.insuranceValidTill);
                    return (
                      <div><p className="text-xs text-gray-500">Insurance Till</p>
                        <p className={`font-medium ${t.cls}`}>{saleDate(car.saleDetails.insuranceValidTill)}{t.note && ` · ${t.note}`}</p>
                      </div>
                    );
                  })()}
                  <div><p className="text-xs text-gray-500">Accident-free</p>
                    <p className={`font-medium ${car.saleDetails?.accidentFree !== false ? 'text-green-600' : 'text-red-600'}`}>
                      {car.saleDetails?.accidentFree !== false ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div><p className="text-xs text-gray-500">RC</p>
                    <p className={`font-medium ${car.saleDetails?.rcAvailable !== false ? 'text-green-600' : 'text-gray-500'}`}>
                      {car.saleDetails?.rcAvailable !== false ? 'Available' : 'Not available'}
                    </p>
                  </div>
                  <div><p className="text-xs text-gray-500">Loan</p>
                    <p className={`font-medium ${LOAN_STATUS[car.saleDetails?.loanStatus]?.cls || 'text-gray-500'}`}>
                      {LOAN_STATUS[car.saleDetails?.loanStatus]?.text || 'Not stated'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Features */}
          {car.features && car.features.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {car.features.map((feature, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Description */}
          {car.description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{car.description}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Pricing & Status */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600">Status</span>
                <select
                  value={car.status || (car.isAvailable ? 'active' : 'inactive')}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    try {
                      const isAvailable = newStatus === 'active';
                      await carAPI.updateCar(car._id, { status: newStatus, isAvailable });
                      setCar(prev => ({ ...prev, status: newStatus, isAvailable }));
                    } catch (err) {
                      console.error('Error updating status:', err);
                    }
                  }}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border-0 cursor-pointer ${
                    (car.status === 'active' || (!car.status && car.isAvailable))
                      ? 'bg-green-100 text-green-700'
                      : car.status === 'maintenance'
                      ? 'bg-yellow-100 text-yellow-700'
                      : car.status === 'booked'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  <option value="active">✅ Available</option>
                  <option value="maintenance">🔧 Maintenance</option>
                  <option value="booked">📅 Booked</option>
                  <option value="inactive">❌ Inactive</option>
                </select>
              </div>
              <div className="text-sm text-gray-500">
                Registration: <span className="font-medium text-gray-900">{car.registrationNumber}</span>
              </div>
              {car.registrationBoard && BOARD_BADGE[car.registrationBoard] && (
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${BOARD_BADGE[car.registrationBoard].cls}`}>
                    {BOARD_BADGE[car.registrationBoard].label}
                  </span>
                </div>
              )}
              {['yellow', 'black'].includes(car.registrationBoard) && car.commercialDetails && (
                <div className="mt-2 text-xs text-gray-600 space-y-0.5 bg-amber-50 border border-amber-100 rounded-lg p-2">
                  {car.commercialDetails.permitType && <div>Permit: <span className="font-medium capitalize text-gray-900">{car.commercialDetails.permitType.replace('-', ' ')}</span></div>}
                  {car.commercialDetails.permitValidTill && <div>Permit valid till: <span className="font-medium text-gray-900">{new Date(car.commercialDetails.permitValidTill).toLocaleDateString('en-IN')}</span></div>}
                  {car.commercialDetails.fitnessValidTill && <div>Fitness valid till: <span className="font-medium text-gray-900">{new Date(car.commercialDetails.fitnessValidTill).toLocaleDateString('en-IN')}</span></div>}
                  <div>Commercial insurance: <span className="font-medium text-gray-900">{car.commercialDetails.commercialInsurance ? 'Yes' : 'No'}</span></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Service Types */}
          {car.serviceTypes && car.serviceTypes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Service Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {car.serviceTypes.map((type, index) => (
                    <Badge key={index} variant="primary">
                      {getServiceTypeLabel(type)}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* A sale-only vehicle has no daily rate — "Per Day ₹0" reads like a bug. */}
              {car.forRent !== false && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Per Day</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(car.pricePerDay)}
                  </span>
                </div>
              )}
              {car.pricePerKm > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Per KM</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(car.pricePerKm)}
                  </span>
                </div>
              )}
              {car.outstationPricePerKm > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Outstation/KM</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(car.outstationPricePerKm)}
                  </span>
                </div>
              )}
              {car.driverBataPerDay > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Driver Bata/Day</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(car.driverBataPerDay)}
                  </span>
                </div>
              )}
              {car.forSale && car.salePrice > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Sale Price</span>
                    <span className="text-xl font-bold text-red-600">
                      {formatCurrency(car.salePrice)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions — hidden for admin (read-only monitor) */}
          {!isAdmin && (
          <Card>
            <CardContent className="p-6 space-y-3">
              <Button className="w-full" onClick={() => navigate('/bookings/create', { state: { carId: car._id } })}>
                Create Booking
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate(`/cars/edit/${car._id}`)}>
                Edit Car
              </Button>
            </CardContent>
          </Card>
          )}
        </div>
      </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Car Documents</h2>
            <Button onClick={() => navigate(`/cars/${id}/documents/upload`)}>
              <Plus className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          </div>

          {documentsLoading ? (
            <div className="py-8">
              <CarLoader size="small" message="Loading documents..." />
            </div>
          ) : documents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No documents uploaded yet</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate(`/cars/${id}/documents/upload`)}>
                  Upload First Document
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Document</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Doc Number</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Authority</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Issued</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Expiry</th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">File</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {documents.map((doc) => {
                      const docUrl = doc.documentUrl || doc.fileUrl;
                      const isImage = docUrl && /\.(jpg|jpeg|png|webp|gif)/i.test(docUrl);
                      const now = new Date();
                      const expiry = doc.expiryDate ? new Date(doc.expiryDate) : null;
                      const isExpired = expiry && expiry < now;
                      const thirtyDays = new Date();
                      thirtyDays.setDate(thirtyDays.getDate() + 30);
                      const isExpiringSoon = expiry && !isExpired && expiry <= thirtyDays;
                      const daysLeft = expiry ? Math.ceil((expiry - now) / (1000 * 60 * 60 * 24)) : null;

                      const typeLabels = {
                        insurance: 'Insurance',
                        registration: 'Registration (RC)',
                        pollution: 'PUC Certificate',
                        fitness: 'Fitness Certificate',
                        permit: 'Permit',
                        other: 'Other'
                      };

                      const typeIcons = {
                        insurance: { bg: 'bg-blue-50', text: 'text-blue-600' },
                        registration: { bg: 'bg-purple-50', text: 'text-purple-600' },
                        pollution: { bg: 'bg-green-50', text: 'text-green-600' },
                        fitness: { bg: 'bg-orange-50', text: 'text-orange-600' },
                        permit: { bg: 'bg-cyan-50', text: 'text-cyan-600' },
                        other: { bg: 'bg-gray-50', text: 'text-gray-600' }
                      };

                      const colors = typeIcons[doc.documentType] || typeIcons.other;

                      return (
                        <tr key={doc._id} className="hover:bg-gray-50 transition-colors">
                          {/* Document Type + Preview */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {isImage && docUrl ? (
                                <a href={docUrl} target="_blank" rel="noopener noreferrer"
                                  className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 hover:opacity-80 transition-opacity">
                                  <img src={docUrl} alt="" className="w-full h-full object-cover" />
                                </a>
                              ) : (
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                                  <FileText className={`h-5 w-5 ${colors.text}`} />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">{typeLabels[doc.documentType] || doc.documentType}</p>
                                {doc.notes && <p className="text-xs text-gray-400 truncate max-w-[150px]">{doc.notes}</p>}
                              </div>
                            </div>
                          </td>

                          {/* Document Number */}
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700 font-mono">{doc.documentNumber || '—'}</span>
                          </td>

                          {/* Authority */}
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">{doc.issuingAuthority || '—'}</span>
                          </td>

                          {/* Issue Date */}
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">
                              {doc.issueDate ? new Date(doc.issueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </span>
                          </td>

                          {/* Expiry Date */}
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600' : 'text-gray-600'}`}>
                              {expiry ? expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3">
                            {isExpired ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">
                                <XCircle className="h-3 w-3" /> Expired
                              </span>
                            ) : isExpiringSoon ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                                <AlertCircle className="h-3 w-3" /> {daysLeft}d left
                              </span>
                            ) : expiry ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-100">
                                <CheckCircle className="h-3 w-3" /> Valid
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          {/* View / Download */}
                          <td className="px-4 py-3 text-center">
                            {docUrl ? (
                              <div className="flex items-center justify-center gap-1">
                                <a
                                  href={docUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                                  title="View document"
                                >
                                  <FileText className="h-4 w-4" />
                                </a>
                                <a
                                  href={docUrl}
                                  download
                                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Service History</h2>
            <Button onClick={() => setServiceModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Service Record
            </Button>
          </div>

          {servicesLoading ? (
            <div className="py-8">
              <CarLoader size="small" message="Loading services..." />
            </div>
          ) : services.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Wrench className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No service records yet</p>
                <Button variant="outline" className="mt-4" onClick={() => setServiceModal(true)}>
                  Add First Service Record
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {services.map((service) => (
                <Card key={service._id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Wrench className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{getMaintenanceTypeLabel(service.serviceType)}</h3>
                          <p className="text-sm text-gray-500 mt-1">{service.description || 'No description'}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(service.serviceDate).toLocaleDateString()}
                            </span>
                            {service.serviceCenterName && (
                              <span>{service.serviceCenterName}</span>
                            )}
                            {service.odometerAtService && (
                              <span className="flex items-center">
                                <Gauge className="h-4 w-4 mr-1" />
                                {service.odometerAtService.toLocaleString()} km
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg">{formatCurrency(service.totalCost || 0)}</p>
                        <Badge variant={service.status === 'completed' ? 'success' : 'warning'}>
                          {service.status}
                        </Badge>
                        <button
                          onClick={() => handleDeleteService(service._id)}
                          className="mt-2 text-red-600 text-sm hover:underline block"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Expenses</h2>
            <Button onClick={() => setExpenseModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </div>

          {/* Expense Summary */}
          {expenseSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(expenseSummary.grandTotal || 0)}
                  </p>
                </CardContent>
              </Card>
              {expenseSummary.categoryBreakdown?.slice(0, 3).map((cat) => (
                <Card key={cat._id}>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">{getExpenseCategoryLabel(cat._id)}</p>
                    <p className="text-xl font-semibold">{formatCurrency(cat.total)}</p>
                    <p className="text-xs text-gray-400">{cat.count} entries</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {expensesLoading ? (
            <div className="py-8">
              <CarLoader size="small" message="Loading expenses..." />
            </div>
          ) : expenses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <IndianRupee className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No expenses recorded yet</p>
                <Button variant="outline" className="mt-4" onClick={() => setExpenseModal(true)}>
                  Add First Expense
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {expenses.map((expense) => (
                      <tr key={expense._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{new Date(expense.date).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary">{getExpenseCategoryLabel(expense.category)}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{expense.description || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(expense.amount)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDeleteExpense(expense._id)}
                            className="text-red-600 text-sm hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* FASTag Tab */}
      {activeTab === 'fastag' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">FASTag Management</h2>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => {
                setFastagForm({
                  fastagId: fastag?.fastagId || '',
                  provider: fastag?.provider || '',
                  balance: fastag?.balance || ''
                });
                setFastagModal(true);
              }}>
                <Edit className="h-4 w-4 mr-2" />
                {fastag ? 'Update FASTag' : 'Setup FASTag'}
              </Button>
              {fastag && (
                <Button onClick={() => setTransactionModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Transaction
                </Button>
              )}
            </div>
          </div>

          {fastagLoading ? (
            <div className="py-8">
              <CarLoader size="small" message="Loading FASTag..." />
            </div>
          ) : !fastag ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">FASTag not configured</p>
                <Button variant="outline" className="mt-4" onClick={() => setFastagModal(true)}>
                  Setup FASTag
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* FASTag Info Card */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Current Balance</p>
                    <p className={`text-2xl font-bold ${fastag.balance < 100 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(fastag.balance || 0)}
                    </p>
                    {fastag.balance < 100 && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Low balance
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">FASTag ID</p>
                    <p className="font-medium">{fastag.fastagId || 'N/A'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Provider</p>
                    <p className="font-medium">{fastag.provider || 'N/A'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Transactions */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Transactions</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {fastagTransactions.length === 0 ? (
                    <div className="py-8 text-center text-gray-500">
                      No transactions recorded
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {fastagTransactions.map((txn) => (
                          <tr key={txn._id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{new Date(txn.date).toLocaleDateString()}</td>
                            <td className="px-4 py-3">
                              <Badge variant={txn.transactionType === 'recharge' ? 'success' : 'warning'}>
                                {txn.transactionType === 'recharge' && <TrendingUp className="h-3 w-3 mr-1" />}
                                {txn.transactionType === 'toll' && <TrendingDown className="h-3 w-3 mr-1" />}
                                {txn.transactionType}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{txn.description || '-'}</td>
                            <td className={`px-4 py-3 text-sm text-right font-medium ${
                              txn.transactionType === 'recharge' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {txn.transactionType === 'recharge' ? '+' : '-'}{formatCurrency(txn.amount)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => handleDeleteTransaction(txn._id)}
                                className="text-red-600 text-sm hover:underline"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Add Service Modal */}
      <Modal
        isOpen={serviceModal}
        onClose={() => setServiceModal(false)}
        title="Add Service Record"
        size="md"
      >
        <form onSubmit={handleAddService} className="space-y-4">
          <Select
            label="Service Type"
            value={serviceForm.serviceType}
            onChange={(e) => setServiceForm({...serviceForm, serviceType: e.target.value})}
            options={[
              { value: 'regular_service', label: 'Regular Service' },
              { value: 'oil_change', label: 'Oil Change' },
              { value: 'tire_change', label: 'Tire Change' },
              { value: 'brake_service', label: 'Brake Service' },
              { value: 'ac_service', label: 'AC Service' },
              { value: 'battery', label: 'Battery' },
              { value: 'engine', label: 'Engine' },
              { value: 'electrical', label: 'Electrical' },
              { value: 'body_repair', label: 'Body Repair' },
              { value: 'painting', label: 'Painting' },
              { value: 'washing', label: 'Washing' },
              { value: 'other', label: 'Other' }
            ]}
          />
          <Input
            label="Service Date"
            type="date"
            value={serviceForm.serviceDate}
            onChange={(e) => setServiceForm({...serviceForm, serviceDate: e.target.value})}
          />
          <Input
            label="Description"
            value={serviceForm.description}
            onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
            placeholder="Brief description of service"
          />
          <Input
            label="Service Center Name"
            value={serviceForm.serviceCenterName}
            onChange={(e) => setServiceForm({...serviceForm, serviceCenterName: e.target.value})}
            placeholder="Enter service center name"
          />
          <Input
            label="Odometer Reading (km)"
            type="number"
            value={serviceForm.odometerAtService}
            onChange={(e) => setServiceForm({...serviceForm, odometerAtService: e.target.value})}
            placeholder="Current odometer reading"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Labor Cost"
              type="number"
              value={serviceForm.laborCost}
              onChange={(e) => setServiceForm({...serviceForm, laborCost: e.target.value})}
              placeholder="0"
            />
            <Input
              label="Parts Cost"
              type="number"
              value={serviceForm.partsCost}
              onChange={(e) => setServiceForm({...serviceForm, partsCost: e.target.value})}
              placeholder="0"
            />
          </div>
          <Select
            label="Status"
            value={serviceForm.status}
            onChange={(e) => setServiceForm({...serviceForm, status: e.target.value})}
            options={[
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' }
            ]}
          />
          <Input
            label="Notes"
            value={serviceForm.notes}
            onChange={(e) => setServiceForm({...serviceForm, notes: e.target.value})}
            placeholder="Additional notes"
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setServiceModal(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Service</Button>
          </div>
        </form>
      </Modal>

      {/* Add Expense Modal */}
      <Modal
        isOpen={expenseModal}
        onClose={() => setExpenseModal(false)}
        title="Add Expense"
        size="md"
      >
        <form onSubmit={handleAddExpense} className="space-y-4">
          <Select
            label="Category"
            value={expenseForm.category}
            onChange={(e) => setExpenseForm({...expenseForm, category: e.target.value})}
            options={[
              { value: 'fuel', label: 'Fuel' },
              { value: 'service', label: 'Service' },
              { value: 'repair', label: 'Repair' },
              { value: 'insurance', label: 'Insurance' },
              { value: 'tax', label: 'Tax' },
              { value: 'fastag', label: 'FASTag' },
              { value: 'parking', label: 'Parking' },
              { value: 'fine', label: 'Fine' },
              { value: 'other', label: 'Other' }
            ]}
          />
          <Input
            label="Amount"
            type="number"
            value={expenseForm.amount}
            onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
            placeholder="Enter amount"
            required
          />
          <Input
            label="Date"
            type="date"
            value={expenseForm.date}
            onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
          />
          <Input
            label="Description"
            value={expenseForm.description}
            onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
            placeholder="Brief description"
          />
          <Select
            label="Payment Method"
            value={expenseForm.paymentMethod}
            onChange={(e) => setExpenseForm({...expenseForm, paymentMethod: e.target.value})}
            options={[
              { value: 'cash', label: 'Cash' },
              { value: 'card', label: 'Card' },
              { value: 'upi', label: 'UPI' },
              { value: 'netbanking', label: 'Net Banking' },
              { value: 'other', label: 'Other' }
            ]}
          />
          <Input
            label="Vendor"
            value={expenseForm.vendor}
            onChange={(e) => setExpenseForm({...expenseForm, vendor: e.target.value})}
            placeholder="Vendor name"
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setExpenseModal(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Expense</Button>
          </div>
        </form>
      </Modal>

      {/* FASTag Setup Modal */}
      <Modal
        isOpen={fastagModal}
        onClose={() => setFastagModal(false)}
        title={fastag ? 'Update FASTag' : 'Setup FASTag'}
        size="md"
      >
        <form onSubmit={handleUpdateFastag} className="space-y-4">
          <Input
            label="FASTag ID"
            value={fastagForm.fastagId}
            onChange={(e) => setFastagForm({...fastagForm, fastagId: e.target.value})}
            placeholder="Enter FASTag ID"
          />
          <Input
            label="Provider"
            value={fastagForm.provider}
            onChange={(e) => setFastagForm({...fastagForm, provider: e.target.value})}
            placeholder="e.g., ICICI, SBI, Paytm"
          />
          <Input
            label="Current Balance"
            type="number"
            value={fastagForm.balance}
            onChange={(e) => setFastagForm({...fastagForm, balance: e.target.value})}
            placeholder="Enter current balance"
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setFastagModal(false)}>
              Cancel
            </Button>
            <Button type="submit">{fastag ? 'Update' : 'Setup'}</Button>
          </div>
        </form>
      </Modal>

      {/* Add Transaction Modal */}
      <Modal
        isOpen={transactionModal}
        onClose={() => setTransactionModal(false)}
        title="Add FASTag Transaction"
        size="md"
      >
        <form onSubmit={handleAddTransaction} className="space-y-4">
          <Select
            label="Transaction Type"
            value={transactionForm.transactionType}
            onChange={(e) => setTransactionForm({...transactionForm, transactionType: e.target.value})}
            options={[
              { value: 'toll', label: 'Toll Payment' },
              { value: 'recharge', label: 'Recharge' }
            ]}
          />
          <Input
            label="Amount"
            type="number"
            value={transactionForm.amount}
            onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
            placeholder="Enter amount"
            required
          />
          <Input
            label="Date"
            type="date"
            value={transactionForm.date}
            onChange={(e) => setTransactionForm({...transactionForm, date: e.target.value})}
          />
          <Input
            label="Description"
            value={transactionForm.description}
            onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
            placeholder="e.g., Toll plaza name, Recharge via UPI"
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setTransactionModal(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Transaction</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Car"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{formatVehicleName(car, { showYear: false })}</strong>? This action cannot be undone.
          </p>
          <div className="flex space-x-3 justify-end">
            <Button variant="outline" onClick={() => setDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal isOpen={confirm.isOpen} onClose={() => setConfirm(p => ({ ...p, isOpen: false }))} onConfirm={confirm.onConfirm} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} />
      <Toast isOpen={toast.isOpen} onClose={() => setToast(p => ({ ...p, isOpen: false }))} title={toast.title} message={toast.message} type={toast.type} />
    </div>
  );
};

export default CarDetails;
